import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const tools = [
  {
    name: 'Name Picker Wheel',
    path: '/tools/name-picker',
    desc: 'Randomly pick a name from a list of names.',
    icon: '👤',
  },
  {
    name: 'Team Picker Wheel',
    path: '/tools/team-picker',
    desc: 'Randomly group names into teams.',
    icon: '👥',
  },
  {
    name: 'Yes No Picker Wheel',
    path: '/tools/yesno-picker',
    desc: 'Randomly pick Yes or No.',
    icon: '✅',
  },
  {
    name: 'Number Picker Wheel',
    path: '/tools/number-picker',
    desc: 'Randomly pick a number from a range or list.',
    icon: '🔢',
  },
  {
    name: 'Letter Picker Wheel',
    path: '/tools/letter-picker',
    desc: 'Randomly pick a letter from A-Z.',
    icon: '🔤',
  },
  {
    name: 'Country Picker Wheel',
    path: '/tools/country-picker',
    desc: 'Randomly pick a country.',
    icon: '🌍',
  },
  {
    name: 'State Picker Wheel',
    path: '/tools/state-picker',
    desc: 'Randomly pick a US state.',
    icon: '🗽',
  },
  {
    name: 'Color Picker Wheel',
    path: '/tools/color-picker',
    desc: 'Randomly pick a color.',
    icon: '🎨',
  },
  {
    name: 'Date Picker Wheel',
    path: '/tools/date-picker',
    desc: 'Randomly pick a date.',
    icon: '📅',
  },
  {
    name: 'Instagram Comment Picker Wheel',
    path: '/tools/instagram-comment-picker',
    desc: 'Randomly pick a winner from Instagram comments.',
    icon: '💬',
  },
  {
    name: 'MLB Picker Wheel',
    path: '/tools/mlb-picker',
    desc: 'Randomly pick a MLB team.',
    icon: '⚾',
  },
  {
    name: 'NBA Picker Wheel',
    path: '/tools/nba-picker',
    desc: 'Randomly pick a NBA team.',
    icon: '🏀',
  },
  {
    name: 'NFL Picker Wheel',
    path: '/tools/nfl-picker',
    desc: 'Randomly pick a NFL team.',
    icon: '🏈',
  },
  {
    name: 'Picker Wheel',
    path: '/tools/picker',
    desc: 'A general random picker wheel for any custom options.',
    icon: '🎡',
  }
]

const ToolsPage: React.FC = () => (
  <>
    <Helmet>
      <title>All AI Decision Maker Tools - Free Online Decision Wheels & Picker Tools | AI Decision Maker</title>
      <meta name="description" content="Explore all AI decision maker tools and picker wheels. From name pickers to sports team selectors, find the perfect AI decision maker for any choice. Free online decision tools with AI analysis." />
      <meta name="keywords" content="ai decision maker tools, picker wheels, decision tools, name picker, team picker, yes no picker, number picker, free decision tools, ai decision assistant, decision wheel tools, online picker, random selector" />
      <meta property="og:title" content="All AI Decision Maker Tools - Free Online Decision Wheels & Picker Tools" />
      <meta property="og:description" content="Explore all AI decision maker tools and picker wheels. Free online decision tools for any choice with AI analysis." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://aidecision.org/tools" />
      <meta property="og:image" content="https://aidecision.org/tools-og-image.png" />
      <meta name="twitter:title" content="All AI Decision Maker Tools - Free Online Decision Wheels" />
      <meta name="twitter:description" content="Explore all AI decision maker tools and picker wheels. Free online decision tools for any choice." />
      <meta name="twitter:image" content="https://aidecision.org/tools-twitter-image.png" />
      <link rel="canonical" href="https://aidecision.org/tools" />
      
      {/* Structured Data for Tools Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "AI Decision Maker Tools",
          "description": "Complete collection of AI decision maker tools and picker wheels",
          "url": "https://aidecision.org/tools",
          "numberOfItems": 14,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Name Picker Wheel",
              "url": "https://aidecision.org/tools/name-picker",
              "description": "Randomly pick a name from a list of names"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Team Picker Wheel",
              "url": "https://aidecision.org/tools/team-picker",
              "description": "Randomly group names into teams"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Yes No Picker Wheel",
              "url": "https://aidecision.org/tools/yesno-picker",
              "description": "Randomly pick Yes or No"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Number Picker Wheel",
              "url": "https://aidecision.org/tools/number-picker",
              "description": "Randomly pick a number from a range or list"
            },
            {
              "@type": "ListItem",
              "position": 5,
              "name": "Letter Picker Wheel",
              "url": "https://aidecision.org/tools/letter-picker",
              "description": "Randomly pick a letter from A-Z"
            },
            {
              "@type": "ListItem",
              "position": 6,
              "name": "Country Picker Wheel",
              "url": "https://aidecision.org/tools/country-picker",
              "description": "Randomly pick a country"
            },
            {
              "@type": "ListItem",
              "position": 7,
              "name": "State Picker Wheel",
              "url": "https://aidecision.org/tools/state-picker",
              "description": "Randomly pick a US state"
            },
            {
              "@type": "ListItem",
              "position": 8,
              "name": "Color Picker Wheel",
              "url": "https://aidecision.org/tools/color-picker",
              "description": "Randomly pick a color"
            },
            {
              "@type": "ListItem",
              "position": 9,
              "name": "Date Picker Wheel",
              "url": "https://aidecision.org/tools/date-picker",
              "description": "Randomly pick a date"
            },
            {
              "@type": "ListItem",
              "position": 10,
              "name": "Instagram Comment Picker Wheel",
              "url": "https://aidecision.org/tools/instagram-comment-picker",
              "description": "Randomly pick a winner from Instagram comments"
            },
            {
              "@type": "ListItem",
              "position": 11,
              "name": "MLB Picker Wheel",
              "url": "https://aidecision.org/tools/mlb-picker",
              "description": "Randomly pick a MLB team"
            },
            {
              "@type": "ListItem",
              "position": 12,
              "name": "NBA Picker Wheel",
              "url": "https://aidecision.org/tools/nba-picker",
              "description": "Randomly pick a NBA team"
            },
            {
              "@type": "ListItem",
              "position": 13,
              "name": "NFL Picker Wheel",
              "url": "https://aidecision.org/tools/nfl-picker",
              "description": "Randomly pick a NFL team"
            },
            {
              "@type": "ListItem",
              "position": 14,
              "name": "General Picker Wheel",
              "url": "https://aidecision.org/tools/picker",
              "description": "A general random picker wheel for any custom options"
            }
          ]
        })}
      </script>
      <meta property="og:url" content="https://aidecision.org/tools" />
      <meta name="twitter:title" content="All AI Decision Maker Tools" />
      <meta name="twitter:description" content="Explore all AI decision maker tools and picker wheels." />
      <link rel="canonical" href="https://aidecision.org/tools" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "AI Decision Maker Tools",
          "description": "Collection of AI decision maker tools and picker wheels",
          "url": "https://aidecision.org/tools",
          "numberOfItems": tools.length,
          "itemListElement": tools.map((tool, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "WebApplication",
              "name": tool.name,
              "description": tool.desc,
              "url": `https://aidecision.org${tool.path}`,
              "applicationCategory": "ProductivityApplication"
            }
          }))
        })}
      </script>
    </Helmet>

    <div className="max-w-3xl mx-auto">
      <header className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">All AI Decision Maker Tools</h1>
        <p className="text-lg text-gray-600">Explore our collection of AI decision maker tools and picker wheels for every decision-making need.</p>
      </header>
      
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6" aria-label="AI Decision Maker Tools">
        {tools.map(tool => (
          <Link to={tool.path} key={tool.path} className="block bg-white rounded-xl shadow-lg p-6 hover:bg-blue-50 transition" aria-label={`Go to ${tool.name}`}>
            <div className="text-4xl mb-2" aria-hidden="true">{tool.icon}</div>
            <div className="text-xl font-semibold mb-1">{tool.name}</div>
            <div className="text-gray-600 text-base">{tool.desc}</div>
          </Link>
        ))}
      </section>
      
      <section className="mt-12 text-center">
        <h2 className="text-2xl font-semibold mb-4">Why Choose AI Decision Maker Tools?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-gray-700">
          <article>
            <h3 className="font-medium text-gray-900 mb-2">AI-Powered Analysis</h3>
            <p>Every AI decision maker tool includes intelligent analysis to help you understand your choices better.</p>
          </article>
          <article>
            <h3 className="font-medium text-gray-900 mb-2">Free & Easy to Use</h3>
            <p>All our AI decision maker tools are completely free and designed for simplicity and effectiveness.</p>
          </article>
          <article>
            <h3 className="font-medium text-gray-900 mb-2">Multiple Decision Modes</h3>
            <p>Choose from normal, elimination, or accumulation modes to match your decision-making style.</p>
          </article>
        </div>
      </section>
    </div>
  </>
)

export default ToolsPage 